import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type {
  Task,
  TaskStatus,
  TaskEntityType,
  CreateTaskInput,
  PatchTaskInput,
} from '@/types/api'

interface TaskFilters {
  status?: TaskStatus
  entity_type?: TaskEntityType
  entity_id?: string
}

export function tasksQueryKey(filters?: TaskFilters) {
  return ['tasks', filters] as const
}

export function useTasks(filters?: TaskFilters) {
  return useQuery({
    queryKey: tasksQueryKey(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.status) params.set('status', filters.status)
      if (filters?.entity_type) params.set('entity_type', filters.entity_type)
      if (filters?.entity_id) params.set('entity_id', filters.entity_id)
      const qs = params.size ? `?${params}` : ''
      return api.get<Task[]>(`/tasks${qs}`)
    },
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<Task>('/tasks', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function usePatchTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PatchTaskInput }) =>
      api.patch<Task>(`/tasks/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}
